import {
  Component,
  OnDestroy,
  inject,
  effect,
  OnInit,
  signal,
  ViewChild,
  TemplateRef,
  input,
  computed,
  untracked,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { TooltipComponent } from '../../../../../shared/components/ui/tooltip/tooltip.component';
import { MultipleBarComponent } from '../../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { IsometricBarComponent } from '../../../../../shared/components/charts/isometric-bar/isometric-bar.component';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { LollipopChartComponent } from '../../../../../shared/components/charts/lollipop-chart/lollipop-chart.component';
import { HorizontalBarChartComponent } from '../../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { VerticalSingleBarComponent } from '../../../../../shared/components/charts/bar-charts/vertical-single-bar/vertical-single-bar.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
// StackedBarComponent not used in this view
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { TransactionLogsPanelComponent, TransactionLogsPanelConfig } from '../../../../../shared/components/transaction-logs-panel/transaction-logs-panel.component';
import { AdminDetailsComponent } from '../admin-details/admin-details.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { IndividualToasterComponent } from '../../../../../shared/components/loader/loader.component';
import { ArrangmentCronApiService } from '../../service/api-service.service';
import { ArrangmentCronDatePickerService } from '../../services/date-picker.service';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import {
  ByAdminRecord,
  CreationTrendRecord,
  DuplicatesByAdminRecord,
  DuplicatesByAdminStats,
  DuplicatesTrendRecord,
  DuplicatesSummaryStatsAPI,
  RecoveryStatusRecord,
  SuccessRateTrendRecord,
} from '../../interface/arrangment-cron-api.interface';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';
import { parseToUtc } from '../../../../../shared/utils/date-utils';
import { ARRANGMENT_CRON_DEFAULT_COL_SPAN } from '../../arrangment-cron-layout.catalog';
import {
  arrangmentSectionColClass,
  arrangmentSectionOrder,
  showArrangmentSection,
} from '../../arrangment-cron-section-layout';
import {
  ARRANGEMENT_CRON_ADMIN_CHART_SECTION_ID,
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS,
  ArrangementCronAdminPerformanceSectionDef,
  ArrangementCronAdminPerformanceSectionId,
  DashboardPageKey,
  isArrangementCronAdminPerformanceSectionId,
} from '../../../../../core/enums/dashboard-cards.enum';
import {
  createEmbeddedSectionFilterByWidgetKeyMap,
  findCatalogPageId,
  resolveActiveCatalogProductId,
  resolveWidgetKeyToSectionId,
} from '../../../../../shared/components/dashboard/dashboard-embed.helpers';
import { CustomDashboardPinService } from '../../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../../../core/services/toaster.service';
import { ApiService } from '../../../../../core/services/api.service';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  normalizeLayoutEditorColSpan,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DashboardLayoutEditorComponent } from '../../../../../shared/components/dashboard/dashboard-layout-editor.component';

export interface StatCard {
  label: string;
  value: string;
  icon: string;
  iconBg: string;
  shadow: string;
}

export interface DetailedStatItem {
  label: string;
  value: string;
  valueColor: string;
  barColor: string;
}

export interface DetailedStat {
  title: string;
  mainValue: string;
  subValue?: string;
  items: DetailedStatItem[];
}

export interface YearSummaryCard {
  year: string;
  label: string;
  status: string;
  statusClass: string;
  duplicates: string;
  deletions: string;
  contracts: string;
  nullEntries: string;
  trend: string;
  trendIcon: string;
  trendClass: string;
}

export interface ChartRowItem {
  id: string;
  title: string;
  subtitle: string;
  type: string;
  height: string;
  minHeight?: number;
  marginTop?: number;
  marginBottom?: number;
  legendOffset?: number;
  legendPosition?: 'left' | 'center' | 'right';
  legendGap?: number;
}

export interface LayoutRow {
  type: 'stats' | 'detailed-stats' | 'charts' | 'year-summary' | 'table';
  gridClass?: string;
  items?: any[];
  title?: string;
  subtitle?: string;
  tableKey?: string;
  headers?: any[];
  data?: any[];
}

/** Persisted layout state per admin-performance section. */
interface AdminPerformanceSectionLayoutItem {
  id: ArrangementCronAdminPerformanceSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

/** Draft row used by the Customize Layout editor. */
interface AdminPerformanceLayoutEditorItem extends AdminPerformanceSectionLayoutItem {
  title: string;
  defaultColSpan?: number | null;
}

// Success-rate bucket ranges for Chart 3
const SUCCESS_RATE_BUCKETS = [
  { label: '0-10%', min: 0, max: 10, color: '#ef4444' },
  { label: '10-20%', min: 10, max: 20, color: '#f97316' },
  { label: '20-30%', min: 20, max: 30, color: '#eab308' },
  { label: '30-40%', min: 30, max: 40, color: '#f87171' },
  { label: '40-50%', min: 40, max: 50, color: '#fbbf24' },
  { label: '50-60%', min: 50, max: 60, color: '#34d399' },
  { label: '60-70%', min: 60, max: 70, color: '#a78bfa' },
  { label: '70-80%', min: 70, max: 80, color: '#f472b6' },
  { label: '80%+', min: 80, max: 101, color: '#60a5fa' },
];

@Component({
  selector: 'app-admin-performance',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    IconComponentV2,
    TooltipComponent,
    MultipleBarComponent,
    IsometricBarComponent,
    LollipopChartComponent,
    HorizontalBarChartComponent,
    VerticalSingleBarComponent,
    LineChartComponent,
    LineAreaChartComponent,
    TransactionLogsPanelComponent,
    ScrollTableComponent,
    IndividualToasterComponent,
    AdminDetailsComponent,
    MatDialogModule,
    DashboardLayoutEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './admin-performance.component.html'
})
export class AdminPerformanceComponent implements OnInit, OnDestroy {
  private readonly apiService = inject(ArrangmentCronApiService);
  private readonly datePickerService = inject(ArrangmentCronDatePickerService);
  private readonly customDashboardPin = inject(CustomDashboardPinService);
  private readonly dashboardLayoutService = inject(DashboardLayoutService);
  private readonly _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private readonly toaster = inject(ToasterService);
  private readonly catalogApiService = inject(ApiService);
  public readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.ARRANGEMENT_CRON_ADMIN_PERFORMANCE;

  readonly SectionId = ArrangementCronAdminPerformanceSectionId;
  get SECTIONS() {
    return ArrangementCronAdminPerformanceSectionDef;
  }

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<
    string,
    ArrangementCronAdminPerformanceSectionId
  > = new Map(
    (
      Object.entries(ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS) as [
        ArrangementCronAdminPerformanceSectionId,
        string,
      ][]
    ).map(([sectionId, backendKey]) => [backendKey, sectionId]),
  );

  readonly layoutMap = signal<
    Record<ArrangementCronAdminPerformanceSectionId, AdminPerformanceSectionLayoutItem>
  >({} as Record<ArrangementCronAdminPerformanceSectionId, AdminPerformanceSectionLayoutItem>);

  readonly orderedSections = computed(() => {
    const sections = this.SECTIONS;
    const layout = this.layoutMap();
    return sections
      .map((s) => layout[s.id as ArrangementCronAdminPerformanceSectionId])
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
  });

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  readonly pinnedSections = signal<Set<ArrangementCronAdminPerformanceSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<ArrangementCronAdminPerformanceSectionId>>(new Set());
  readonly pinnableWidgetIds = signal<Map<ArrangementCronAdminPerformanceSectionId, number>>(
    new Map(),
  );

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<AdminPerformanceLayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_arrangment_cron_ap_layout_v1';
  private _layoutSnapshot: Record<
    ArrangementCronAdminPerformanceSectionId,
    AdminPerformanceSectionLayoutItem
  > | null = null;

  showSection(sectionId: string): boolean {
    if (this.mixedMode()) {
      return this.embeddedVisibleSections().some((s) => s.id === sectionId);
    }
    return showArrangmentSection(this._layoutRecord(), sectionId);
  }

  sectionOrder(sectionId: string): number {
    return arrangmentSectionOrder(this._layoutRecord(), sectionId);
  }

  getSectionColClass(sectionId: string): string {
    if (this.mixedMode()) return 'col-span-12';
    return arrangmentSectionColClass(this._layoutRecord(), sectionId);
  }

  private _layoutRecord(): Record<string, AdminPerformanceSectionLayoutItem> {
    return this.layoutMap() as unknown as Record<string, AdminPerformanceSectionLayoutItem>;
  }

  chartSectionId(chartItemId: string): ArrangementCronAdminPerformanceSectionId {
    return (
      ARRANGEMENT_CRON_ADMIN_CHART_SECTION_ID[chartItemId] ??
      (`ac_ap_${chartItemId.replace(/-/g, '_')}` as ArrangementCronAdminPerformanceSectionId)
    );
  }

  detailedStatAt(index: number) {
    return this.detailedStatsRow()?.items?.[index] ?? null;
  }

  yearSummaryAt(index: number) {
    return this.yearSummaryCards()[index] ?? null;
  }

  statsRow(): LayoutRow | undefined {
    return this.contentRows.find((r) => r.type === 'stats');
  }

  detailedStatsRow(): LayoutRow | undefined {
    return this.contentRows.find((r) => r.type === 'detailed-stats');
  }

  yearSummaryRow(): LayoutRow | undefined {
    return this.contentRows.find((r) => r.type === 'year-summary');
  }

  tableRow(tableKey: 'exposure' | 'detailed'): LayoutRow | undefined {
    return this.contentRows.find((r) => r.type === 'table' && r.tableKey === tableKey);
  }

  readonly orderedVisibleChartItems = computed(() => {
    const layout = this._layoutRecord();
    const charts = this.contentRows
      .filter((r) => r.type === 'charts')
      .flatMap((r) => (r.items ?? []) as ChartRowItem[]);
    if (!layout || !Object.keys(layout).length) return charts;
    return charts
      .filter((item) => this.showSection(this.chartSectionId(item.id)))
      .sort(
        (a, b) =>
          arrangmentSectionOrder(layout, this.chartSectionId(a.id)) -
          arrangmentSectionOrder(layout, this.chartSectionId(b.id)),
      );
  });

  @ViewChild('adminDetailsModal') adminDetailsModal!: TemplateRef<any>;

  // Date-picker signals (shared with Trends component)
  readonly startDate = this.datePickerService.startDate;
  readonly endDate = this.datePickerService.endDate;
  readonly unit = this.datePickerService.unit;

  // ─── Loading flags ──────────────────────────────────────────────────────────
  isLoadingDuplicatesTrend = false;
  isLoadingDuplicatesByAdmin = false;
  isLoadingByAdmin = false;
  isLoadingDuplicatesSummary = false;
  isLoadingRecoveryStatus = false;
  isLoadingRepeatTrend = false;
  isLoadingYearlySummary = false;
  isAdminPerformanceTableLoading = false;
  selectedAdminId: number | null = null;
  adminOptions: { value: string; label: string }[] = [];

  // Chart Controls
  lollipopHoverBarWidth = 80;
  lollipopBadgeMinWidth = 60;

  // ─── KPI Cards (dynamic) ────────────────────────────────────────────────────
  performanceCards: any[] = [
    { label: 'Active Admins', value: '—', icon: 'userRoundCheck', iconBg: 'bg-slate-950', shadow: 'shadow-slate-500/30' },
    { label: 'Total Recovered Amount', value: '—', icon: 'rotateCw', iconBg: 'bg-amber-500', shadow: 'shadow-amber-500/20' },
    { label: 'Total Contracts', value: '—', icon: 'fileText', iconBg: 'bg-blue-600', shadow: 'shadow-blue-500/20' },
    { label: 'High Value Contracts', value: '—', icon: 'fileText', iconBg: 'bg-purple-600', shadow: 'shadow-purple-500/20' },
    { label: 'Repeat Arrangements %', value: '—', icon: 'repeat', iconBg: 'bg-emerald-500', shadow: 'shadow-emerald-500/20' },
    { label: 'High Risk Contracts', value: '—', icon: 'triangleAlert', iconBg: 'bg-rose-500', shadow: 'shadow-rose-500/20' },
  ];

  detailedStats: any[] = [
    {
      title: 'Top Performer', mainValue: '—', subValue: '', items: [
        { label: 'Recovered', value: '—', valueColor: 'text-emerald-500', barColor: 'bg-emerald-500' },
        { label: 'Success Rate', value: '—', valueColor: 'text-emerald-500', barColor: 'bg-emerald-500' },
      ]
    },
    {
      title: 'Duplicate Crisis', mainValue: '—', subValue: '(Total)', items: [
        { label: 'Duplicate Cost', value: '—', valueColor: 'text-rose-500', barColor: 'bg-rose-500' },
        { label: 'High Duplicate Admins', value: '—', valueColor: 'text-rose-500', barColor: 'bg-rose-500' },
      ]
    },
    {
      title: 'Avg. Recovery Per Admin', mainValue: '—', subValue: '', items: [
        { label: 'Avg. Recovered/Arrangement', value: '—', valueColor: 'text-blue-600', barColor: 'bg-blue-600' },
        { label: 'Recovery Efficiency', value: '—', valueColor: 'text-blue-600', barColor: 'bg-blue-600' },
      ]
    },
  ];

  // ─── Chart 1: Duplicate Entry Trend Over Time (Mixed Bar + Line) ────────────
  duplicateOverTimeData: any = { data1: [], data2: [], data3: [] };

  chartLegendData: any[] = [
    { label: 'Total Null Entries', color: '#eab308' },
    { label: 'Deletion Entries', color: '#ec4899' },
    { label: 'Duplicate Entries', color: '#3b82f6' },
  ];

  chartColors: any = {
    data1: '#eab308',
    data2: '#ec4899',
    data3: '#3b82f6',
    data4: ''
  };

  // ─── Chart 2: Duplicate Trend YoY (Area Chart) ──────────────────────────────
  duplicateYoYData: any[] = [];

  yoYTheme = { seriesColors: ['#3b82f6'] };

  // ─── Chart 3: Performance Distribution (Isometric Bar) ──────────────────────
  performanceDistributionData: any[] = [];

  performanceStats: any[] = [
    { label: 'Peak Performance', value: '—', sub: '' },
    { label: 'Distribution', value: '—', sub: '' },
    { label: 'Low Performers', value: '—', sub: 'Threshold: <30%' },
  ];

  decisionInsight: string = 'Healthy base (Low-performance group under control.)';

  // ─── Chart 4: Recovery Efficiency (Line Area) ─────────────────────────────
  // Driven by getRecoveryStatus (recovered_amount per period), split into
  // Recurring / Partial proportions using getSuccessRateTrend success rates.
  recoveryEfficiencyData: ILineAreaSeries[] = [];

  recoveryEfficiencyTheme = { seriesColors: ['#d946ef', '#3b82f6'] };

  // ─── Chart 5: Repeat Arrangement Pattern (Lollipop + Line) ─────────────────
  repeatArrangementData: any[] = [];
  repeatArrangementLineData: any[] = [];

  // ─── Chart 6: Admin Workload vs Recovery Output (Stacked Horizontal Bar) ────
  adminWorkloadData: any[] = [];

  adminWorkloadLegend: any[] = [
    { label: 'Active Entries', color: '#10b981' },
    { label: 'Duplicate Contracts', color: '#3b82f6' },
    { label: 'Recovery Entries', color: '#f59e0b' },
  ];

  // ─── Chart 7: Admin Productivity vs Recovery Yield (Vertical Single Bar) ─────
  adminProductivityData: any[] = [];

  // ─── Chart 8: Duplicate Entries by Admin (Multiple Bar) ──────────────────────
  duplicatesData: any[] = [];
  deletionsData: any[] = [];

  // ─── Chart 9: Repeat Arrangement Intensity Trend (Line) ──────────────────────
  repeatArrangementTrendData: any[] = [];

  // ─── Year Summary Cards ───────────────────────────────────────────────────────
  yearSummaryCards = signal<any[]>([]);

  // ─── Tables / exposure ───────────────────────────────────────────────────────
  adminExposureData: any[] = [];
  detailedDuplicateData: any[] = [];

  exposureHeaders: TableHeader[] = [
    { key: 'name', label: 'Admin', align: 'text-left' },
    { key: 'affected', label: 'Contracts Affected', align: 'text-left' },
    { key: 'rate', label: 'Admin Duplicate Rate', align: 'text-right', textClass: 'font-bold' },
    { key: 'action', label: 'Action', align: 'text-center', showActionTooltip: true, actionTooltip: 'View Details' },
  ];

  detailedHeaders: TableHeader[] = [
    { key: 'name', label: 'Admin', align: 'text-left' },
    { key: 'nullEntries', label: 'Null Entries', align: 'text-left', textClass: 'text-amber-500 font-bold' },
    { key: 'duplicates', label: 'Duplicates', align: 'text-left', textClass: 'text-rose-500 font-bold' },
    { key: 'deletions', label: 'Deletions', align: 'text-left' },
    { key: 'contracts', label: 'Contracts', align: 'text-left' },
    { key: 'avg', label: 'Avg Per Contract', align: 'text-left', textClass: 'font-bold' },
    { key: 'impact', label: 'Impact', align: 'text-right' },
    { key: 'action', label: 'Action', align: 'text-center', showActionTooltip: true, actionTooltip: 'View Details' },
  ];

  contentRows: LayoutRow[] = [];

  isPanelOpen = false;
  panelConfig: TransactionLogsPanelConfig | null = null;

  isAdminDetailsPanelOpen = false;

  private allAdminPerformanceRecords: ByAdminRecord[] = [];
  private subscriptions: Subscription[] = [];

  constructor() {
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.catalogApiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.catalogApiService);
          const pageId = findCatalogPageId(
            catalog as any[],
            this.PAGE_KEY,
            activeProductPid,
          );
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<ArrangementCronAdminPerformanceSectionId>();
          keys.forEach((k) => {
            const sectionId = resolveWidgetKeyToSectionId(k, this.WIDGET_KEY_TO_SECTION);
            if (
              sectionId != null &&
              isArrangementCronAdminPerformanceSectionId(sectionId)
            ) {
              pinned.add(sectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.catalogApiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<ArrangementCronAdminPerformanceSectionId, number>();
            for (const sec of Object.values(ArrangementCronAdminPerformanceSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    // Reactive: re-fetch whenever the date picker changes
    effect(() => {
      const start = this.startDate();
      const end = this.endDate();
      const unit = this.unit();

      if (start && end) {
        this.fetchAllData(start, end, unit);
      }
    });
  }

  ngOnInit(): void {
    this.initializeLayout();
    this._initLayout();
    this.fetchYearlySummaryData();
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    this.datePickerService.updateDateRange(event);
    this.cdr.markForCheck();
  }

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  private fetchAllData(start: string, end: string, unit: string): void {
    const startTime = dayjs.utc(start).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    const endTime = dayjs.utc(end).tz(LA_TIMEZONE).format('YYYY-MM-DD');

    // Cancel any in-flight subscriptions from the previous run
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];

    // Determine which API calls are needed based on visible sections
    const needsDuplicatesTrend = this.showSection(this.chartSectionId('duplicate-over-time')) || this.showSection(this.chartSectionId('duplicate-yoy'));
    const needsDuplicatesByAdmin = this.showSection(this.SectionId.ADMIN_WORKLOAD_VS_RECOVERY) || this.showSection(this.SectionId.DUPLICATE_ENTRIES_BY_ADMIN) || this.showSection(this.SectionId.ADMIN_DUPLICATE_EXPOSURE) || this.showSection(this.SectionId.DETAILED_DUPLICATE_ANALYSIS);
    const needsByAdmin = this.showSection(this.SectionId.PERFORMANCE_DISTRIBUTION_SUCCESS) || this.showSection(this.SectionId.REPEAT_ARRANGEMENT_PATTERN) || this.showSection(this.SectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY) || this.showSection(this.SectionId.STATS_SUMMARY_OVERVIEW) || this.showSection(this.SectionId.TOP_PERFORMER) || this.showSection(this.SectionId.AVG_RECOVERY_PER_ADMIN);
    const needsDuplicatesSummary = this.showSection(this.SectionId.DUPLICATE_CRISIS);
    const needsRecoveryStatus = this.showSection(this.SectionId.RECOVERY_EFFICIENCY);
    const needsRepeatTrend = this.showSection(this.SectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD);

    // ── 1. Duplicates Trend ──────────────────────────────────────────────────
    if (needsDuplicatesTrend) {
      this.isLoadingDuplicatesTrend = true;
      const trendSub = this.apiService.getDuplicatesTrend(
        { start_time: startTime, end_time: endTime, unit: unit as any, is_dummy: 0, outputFormat: 'db' },
        { showLoader: false }
      ).pipe(finalize(() => { this.isLoadingDuplicatesTrend = false; this.cdr.markForCheck(); }))
        .subscribe({
          next: (res) => this.buildDuplicateTrendCharts(res?.records ?? []),
          error: (err) => console.error('getDuplicatesTrend error:', err),
        });
      this.subscriptions.push(trendSub);
    }

    // ── 2. Duplicates by Admin ───────────────────────────────────────────────
    if (needsDuplicatesByAdmin) {
      this.isLoadingDuplicatesByAdmin = true;
      const byAdminDupSub = this.apiService.getDuplicatesByAdmin(
        { start_time: startTime, end_time: endTime, is_dummy: 0 },
        { showLoader: false }
      ).pipe(finalize(() => { this.isLoadingDuplicatesByAdmin = false; this.cdr.markForCheck(); }))
        .subscribe({
          next: (res) => this.buildAdminWorkloadAndDuplicates(res?.records ?? [], res?.stats),
          error: (err) => console.error('getDuplicatesByAdmin error:', err),
        });
      this.subscriptions.push(byAdminDupSub);
    }

    // ── 3. By Admin (performance) ────────────────────────────────────────────
    if (needsByAdmin) {
      this.isLoadingByAdmin = true;
      const byAdminSub = this.apiService.getByAdmin(
        { start_time: startTime, end_time: endTime, is_dummy: 0 },
        { showLoader: false }
      ).pipe(finalize(() => { this.isLoadingByAdmin = false; this.cdr.markForCheck(); }))
        .subscribe({
          next: (res) => this.buildAdminPerformanceCharts(res?.records ?? [], res?.stats),
          error: (err) => console.error('getByAdmin error:', err),
        });
      this.subscriptions.push(byAdminSub);
    }

    // ── 4. Duplicates Summary Stats (for detailed-stats box) ─────────────────
    if (needsDuplicatesSummary) {
      this.isLoadingDuplicatesSummary = true;
      const summaryStatsSub = this.apiService.getDuplicatesSummaryStats(
        { start_time: startTime, end_time: endTime, is_dummy: 0 },
        { showLoader: false }
      ).pipe(finalize(() => { this.isLoadingDuplicatesSummary = false; this.cdr.markForCheck(); }))
        .subscribe({
          next: (res) => this.buildDuplicateDetailedStats(res),
          error: (err) => console.error('getDuplicatesSummaryStats error:', err),
        });
      this.subscriptions.push(summaryStatsSub);
    }

    // ── 5. Recovery Status → Chart 4 (Recovery Efficiency) ──────────────────
    if (needsRecoveryStatus) {
      this.isLoadingRecoveryStatus = true;
      const recoveryStatusSub = this.apiService.getRecoveryStatus(
        { start_time: startTime, end_time: endTime, unit: unit as any, is_dummy: 0, outputFormat: 'db' },
        { showLoader: false }
      ).subscribe({
        next: (statusRes) => {
          const srSub = this.apiService.getSuccessRateTrend(
            { start_time: startTime, end_time: endTime, unit: unit as any, is_dummy: 0, outputFormat: 'db' },
            { showLoader: false }
          ).pipe(finalize(() => { this.isLoadingRecoveryStatus = false; this.cdr.markForCheck(); }))
            .subscribe({
              next: (srRes) => this.buildRecoveryEfficiencyData(
                statusRes?.records ?? [],
                srRes?.records ?? []
              ),
              error: (err) => {
                console.error('getSuccessRateTrend error (for Chart 4):', err);
                this.buildRecoveryEfficiencyData(statusRes?.records ?? [], []);
              },
            });
          this.subscriptions.push(srSub);
        },
        error: (err) => {
          console.error('getRecoveryStatus error:', err);
          this.isLoadingRecoveryStatus = false;
          this.cdr.markForCheck();
        },
      });
      this.subscriptions.push(recoveryStatusSub);
    }

    // ── 6. Creation Trend → Chart 9 (Repeat Intensity Trend) ────────────────
    if (needsRepeatTrend) {
      this.isLoadingRepeatTrend = true;
      const creationSub = this.apiService.getCreationTrend(
        { start_time: startTime, end_time: endTime, unit: unit as any, is_dummy: 0, outputFormat: 'db' },
        { showLoader: false }
      ).pipe(finalize(() => { this.isLoadingRepeatTrend = false; this.cdr.markForCheck(); }))
        .subscribe({
          next: (res) => this.buildRepeatIntensityTrendChart(res?.records ?? []),
          error: (err) => console.error('getCreationTrend error:', err),
        });
      this.subscriptions.push(creationSub);
    }
  }

  /**
   * Fetches historical yearly summary from 2020 to present.
   * This is called once and is immune to date-picker changes.
   */
  private fetchYearlySummaryData(): void {
    if (!this.showSection(this.SectionId.WORST_YEAR) && !this.showSection(this.SectionId.BEST_YEAR) && !this.showSection(this.SectionId.LATEST_YEAR)) {
      return;
    }

    const start = '2020-01-01';
    const end = dayjs.utc().format('YYYY-MM-DD');

    this.isLoadingYearlySummary = true;
    this.apiService.getDuplicatesTrend(
      { start_time: start, end_time: end, unit: 'year', is_dummy: 0, outputFormat: 'db' },
      { showLoader: false }
    ).pipe(finalize(() => this.isLoadingYearlySummary = false))
      .subscribe({
        next: (res) => this.processYearlySummary(res?.records ?? []),
        error: (err) => console.error('fetchYearlySummaryData error:', err),
      });
  }

  private processYearlySummary(records: DuplicatesTrendRecord[]): void {
    if (!records.length) return;

    // Map units to years
    const yearMap = new Map<string, { null_entries: number; duplicates: number; deletions: number; contracts: number }>();
    records.forEach(r => {
      const year = dayjs.utc(r.unit_date).format('YYYY');
      yearMap.set(year, {
        null_entries: r.total_null_entries || 0,
        duplicates: r.duplicate_entries || 0,
        deletions: r.deletion_entries || 0,
        contracts: r.contracts_affected || 0,
      });
    });

    const sortedYears = Array.from(yearMap.keys()).sort();
    this.buildYearSummaryCards(yearMap, sortedYears);
  }

  // ─── Transform helpers ──────────────────────────────────────────────────────

  /**
   * Chart 1, Chart 2, Year Summary Cards
   * Source: getDuplicatesTrend → records
   */
  private buildDuplicateTrendCharts(records: DuplicatesTrendRecord[]): void {
    if (!records.length) return;

    const currentUnit = this.unit();
    // Maps for different granularities: unitMap for the main chart, yearMap for summary cards
    const unitMap = new Map<string, { label: string; null_entries: number; duplicates: number; deletions: number; contracts: number; start: string; end: string }>();
    const yearMap = new Map<string, { null_entries: number; duplicates: number; deletions: number; contracts: number }>();

    records.forEach(r => {
      const d = dayjs.utc(r.unit_date ?? r.start ?? '');
      if (!d.isValid()) return;

      // 1. Maintain yearly aggregation for summary cards
      const year = d.format('YYYY');
      if (!yearMap.has(year)) yearMap.set(year, { null_entries: 0, duplicates: 0, deletions: 0, contracts: 0 });
      const yearEntry = yearMap.get(year)!;
      yearEntry.null_entries += Number(r.total_null_entries) || 0;
      yearEntry.duplicates += Number(r.duplicate_entries) || 0;
      yearEntry.deletions += Number(r.deletion_entries) || 0;
      yearEntry.contracts += Number(r.contracts_affected) || 0;

      // 2. Unit-based aggregation for Chart 1
      let groupKey = '';
      let label = '';
      if (currentUnit === 'day') {
        groupKey = d.format('YYYY-MM-DD');
        label = d.format('DD MMM');
      } else if (currentUnit === 'week') {
        groupKey = d.startOf('week').format('YYYY-MM-DD');
        label = 'W' + d.format('ww') + ' ' + d.format('YYYY');
      } else if (currentUnit === 'month') {
        groupKey = d.format('YYYY-MM');
        label = d.format('MMM YYYY');
      } else if (currentUnit === 'quarter') {
        const q = Math.ceil((d.month() + 1) / 3);
        groupKey = `${d.year()}-Q${q}`;
        label = `Q${q} ${d.year()}`;
      } else {
        groupKey = year;
        label = year;
      }

      if (!unitMap.has(groupKey)) {
        // For quarter, compute start/end manually (dayjs doesn't support startOf('quarter'))
        let startDate = d.startOf(currentUnit as any).format('YYYY-MM-DD');
        let endDate = d.endOf(currentUnit as any).format('YYYY-MM-DD');
        if (currentUnit === 'quarter') {
          const q = Math.ceil((d.month() + 1) / 3);
          const startMonth = ((q - 1) * 3) + 1;
          startDate = `${d.year()}-${String(startMonth).padStart(2, '0')}-01`;
          endDate = parseToUtc(startDate).add(2, 'month').endOf('month').format('YYYY-MM-DD');
        }
        unitMap.set(groupKey, {
          label,
          null_entries: 0,
          duplicates: 0,
          deletions: 0,
          contracts: 0,
          start: startDate,
          end: endDate
        });
      }
      const unitEntry = unitMap.get(groupKey)!;
      unitEntry.null_entries += Number(r.total_null_entries) || 0;
      unitEntry.duplicates += Number(r.duplicate_entries) || 0;
      unitEntry.deletions += Number(r.deletion_entries) || 0;
      unitEntry.contracts += Number(r.contracts_affected) || 0;
    });

    const sortedUnitKeys = Array.from(unitMap.keys()).sort();
    const sortedYears = Array.from(yearMap.keys()).sort();

    // Chart 1: Duplicate Entry Trend Over Time (Respects current unit)
    const data1: any[] = [];
    const data2: any[] = [];
    const data3: any[] = [];

    sortedUnitKeys.forEach(key => {
      const v = unitMap.get(key)!;
      data1.push({ label: v.label, value: v.null_entries, color: '#eab308', start: v.start, end: v.end });
      data2.push({ label: v.label, value: v.deletions, color: '#ec4899', start: v.start, end: v.end });
      data3.push({ label: v.label, value: v.duplicates, color: '#3b82f6', type: 'line', start: v.start, end: v.end });
    });

    this.duplicateOverTimeData = { data1, data2, data3 };

    // Chart 2 — Unit over Unit area
    this.duplicateYoYData = [{
      key: 'duplicates',
      name: 'Duplicate Entries',
      data: sortedUnitKeys.map(key => {
        const v = unitMap.get(key)!;
        return {
          date: v.start,
          value: v.duplicates,
        };
      }),
      areaColor: '#3b82f6',
      lineColor: '#3b82f6',
      areaOpacity: 0.2,
      showPoints: false,
    }];

    this.refreshLayoutData();
  }

  /**
   * Year Summary Cards: best year (fewest duplicates), worst year (most), current year
   */
  private buildYearSummaryCards(
    yearMap: Map<string, { null_entries: number; duplicates: number; deletions: number; contracts: number }>,
    sortedYears: string[]
  ): void {
    if (!sortedYears.length) return;

    const currentYear = String(parseToUtc(undefined).year());

    // 1. Find Worst Year (Max Duplicates)
    let worstYear = sortedYears[0];
    sortedYears.forEach(y => {
      if (yearMap.get(y)!.duplicates > yearMap.get(worstYear)!.duplicates) worstYear = y;
    });

    // 2. Find Best Year (Min Duplicates, excluding years with 0 if possible)
    // Tie-break order: fewest duplicates → fewest deletions → fewest null entries → most recent year
    let bestYear = sortedYears[0];
    sortedYears.forEach(y => {
      const v = yearMap.get(y)!;
      const bestV = yearMap.get(bestYear)!;

      const isBetter = (): boolean => {
        // A zero-duplicate year is skipped when a non-zero candidate already exists
        // (zero likely means no data for that year rather than a perfect year)
        if (v.duplicates === 0 && bestV.duplicates > 0) return false;
        // Prefer a year with actual data over a zero-data placeholder
        if (bestV.duplicates === 0 && v.duplicates > 0) return true;
        // Primary criterion: fewer duplicates
        if (v.duplicates !== bestV.duplicates) return v.duplicates < bestV.duplicates;
        // Tie-break 1: fewer deletions
        if (v.deletions !== bestV.deletions) return v.deletions < bestV.deletions;
        // Tie-break 2: fewer null entries
        if (v.null_entries !== bestV.null_entries) return v.null_entries < bestV.null_entries;
        // Tie-break 3: prefer the more recent year
        return y > bestYear;
      };

      if (isBetter()) bestYear = y;
    });

    const getTrendInfo = (year: string) => {
      const idx = sortedYears.indexOf(year);
      const prevYear = sortedYears[idx - 1];
      const v = yearMap.get(year)!;
      const prevV = prevYear ? yearMap.get(prevYear) : null;

      let trend = 'Stable';
      let trendIcon = 'minus';
      let trendClass = 'text-slate-400';

      if (prevV && prevV.duplicates > 0) {
        const diff = ((v.duplicates - prevV.duplicates) / prevV.duplicates) * 100;
        if (diff > 5) {
          trend = `+${diff.toFixed(0)}%`;
          trendIcon = 'trending-up';
          trendClass = 'text-rose-500';
        } else if (diff < -5) {
          trend = `${diff.toFixed(0)}%`;
          trendIcon = 'trending-down';
          trendClass = 'text-emerald-500';
        }
      }
      return { trend, trendIcon, trendClass };
    };

    const makeCard = (year: string, label: string, status: string, statusClass: string) => {
      const v = yearMap.get(year)!;
      const t = getTrendInfo(year);
      return {
        year, label, status, statusClass,
        duplicates: String(v.duplicates.toLocaleString()),
        deletions: String(v.deletions.toLocaleString()),
        contracts: String(v.contracts.toLocaleString()),
        nullEntries: String(v.null_entries.toLocaleString()),
        ...t
      };
    };

    const cards: any[] = [];

    // Card 1: Worst Year
    cards.push(makeCard(worstYear, 'Worst Year', 'Critical', 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400'));

    // Card 2: Best Year
    cards.push(makeCard(bestYear, 'Best Year', 'Success', 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'));

    // Card 3: Current or Latest Year
    if (yearMap.has(currentYear)) {
      cards.push(makeCard(currentYear, 'Current Year', 'In Progress', 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'));
    } else {
      const latestYear = sortedYears[sortedYears.length - 1];
      cards.push(makeCard(latestYear, latestYear === bestYear || latestYear === worstYear ? 'Status Update' : 'Latest Year', 'In Progress', 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'));
    }

    this.yearSummaryCards.set(cards.slice(0, 3));
    this.refreshLayoutData();
  }

  private records_avg_duplicates(map: Map<string, any>): number {
    let total = 0;
    map.forEach(v => total += v.duplicates);
    return map.size > 0 ? total / map.size : 0;
  }

  /**
   * Chart 6 (admin workload), Chart 8 (duplicates by admin),
   * adminExposureData, detailedDuplicateData
   * Source: getDuplicatesByAdmin → records, stats
   */
  private buildAdminWorkloadAndDuplicates(records: DuplicatesByAdminRecord[], stats?: DuplicatesByAdminStats): void {
    if (!records.length) return;

    // Sort by duplicate_entries desc
    const sorted = [...records].sort((a, b) => (b.duplicate_entries || 0) - (a.duplicate_entries || 0));

    // Chart 6 — Stacked horizontal bar (top 9 admins)
    this.adminWorkloadData = sorted.slice(0, 9).map(r => {
      const active = Math.max(0, (r.contracts_affected || 0) - (r.duplicate_entries || 0) - (r.deletion_entries || 0));
      const duplicates = r.duplicate_entries || 0;
      const total = active + duplicates + (r.deletion_entries || 0);
      return {
        label: r.admin_name,
        value: total,
        segments: [
          { value: active, color: '#10b981', label: 'Active Entries' },
          { value: duplicates, color: '#3b82f6', label: 'Duplicate Contracts' },
          { value: r.deletion_entries || 0, color: '#f59e0b', label: 'Recovery Entries' },
        ],
      };
    });

    // Chart 8 — Top 5 by duplicates / deletions
    const top5 = sorted.slice(0, 5);
    this.duplicatesData = top5.map(r => ({ label: r.admin_name, value: r.duplicate_entries || 0 }));
    this.deletionsData = top5.map(r => ({ label: r.admin_name, value: r.deletion_entries || 0 }));

    // Admin Exposure Table — top 6 by contracts_affected
    const totalAffected = stats?.total_duplicate_entries || 1;
    const topExposed = [...records].sort((a, b) => (b.contracts_affected || 0) - (a.contracts_affected || 0)).slice(0, 6);

    this.adminExposureData = topExposed.map(r => ({
      admin_id: r.admin_id,
      name: r.admin_name,
      affected: r.contracts_affected || 0,
      rate: totalAffected > 0
        ? `${(((r.duplicate_entries || 0) / totalAffected) * 100).toFixed(2)}%`
        : '0.00%',
      actionIconV2: 'eye',
      actionIconClass: 'text-gray-500 hover:text-blue-500 cursor-pointer',
    }));

    // Detailed Duplicate Analysis Table
    const getImpact = (dupes: number): { label: string; cls: string } => {
      if (dupes > 500) return { label: 'High', cls: 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400' };
      if (dupes > 100) return { label: 'Medium', cls: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' };
      return { label: 'Low', cls: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' };
    };

    this.detailedDuplicateData = sorted.map(r => {
      const impact = getImpact(r.duplicate_entries || 0);
      return {
        admin_id: r.admin_id,
        name: r.admin_name,
        nullEntries: r.total_null_entries || 0,
        duplicates: r.duplicate_entries || 0,
        deletions: r.deletion_entries || 0,
        contracts: r.contracts_affected || 0,
        avg: r.contracts_affected > 0
          ? ((r.duplicate_entries || 0) / r.contracts_affected).toFixed(2)
          : '0.00',
        impact: impact.label,
        impactClass: impact.cls,
        actionIconV2: 'eye',
        actionIconClass: 'text-gray-500 hover:text-blue-500 cursor-pointer',
      };
    });

    this.refreshLayoutData();
  }

  /** Recovered amount from by_admin record (API may use either field name). */
  private getAdminRecoveredAmount(record: ByAdminRecord): number {
    return Number(record.total_recovered_amount ?? record['recovered_amount']) || 0;
  }

  /**
   * Top performer by performance_score; if every admin shares the same score, use index 0.
   */
  private pickTopPerformer(records: ByAdminRecord[]): ByAdminRecord {
    if (records.length <= 1) return records[0];
    const scores = records.map(r => Number(r.performance_score) || 0);
    if (scores.every(s => s === scores[0])) return records[0];
    return records.reduce((best, r) =>
      (Number(r.performance_score) || 0) > (Number(best.performance_score) || 0) ? r : best
    );
  }

  /**
   * Chart 3 (performance distribution), Chart 5 (repeat pattern),
   * Chart 7 (admin productivity), Chart 9 (intensity trend),
   * performanceCards, detailedStats (partially from here)
   * Source: getByAdmin → records, stats
   */
  private buildAdminPerformanceCharts(records: ByAdminRecord[], stats?: any): void {
    if (!records.length) return;
    this.allAdminPerformanceRecords = records;
    this.adminOptions = records.map(admin => ({
      value: admin.admin_id.toString(),
      label: admin.admin_name
    }));

    // ── KPI Cards ────────────────────────────────────────────────────────────
    const totalAdmins = stats?.total_admins || records.length;
    const totalArrangements = stats?.total_arrangements || 0;
    const avgSuccessRate = stats?.avg_success_rate || 0;
    const totalRecovered = records.reduce((s, r) => s + (r.total_recovered_amount || 0), 0);
    const uniqueContracts = records.reduce((s, r) => s + (r.unique_contracts || 0), 0);
    const highValueContracts = records.filter(r => (r.total_recovered_amount || 0) > 10000).length;
    const repeatThreshold = totalArrangements > 0 ? (records.reduce((s, r) => s + (r.total_created || 0), 0) - records.length) / totalArrangements * 100 : 0;
    const highRisk = records.filter(r => (r.success_rate || 0) < 30).length;

    this.performanceCards = [
      { label: 'Active Admins', value: String(totalAdmins), icon: 'userRoundCheck', iconBg: 'bg-slate-950', shadow: 'shadow-slate-500/30' },
      { label: 'Total Recovered Amount', value: `$${totalRecovered.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, icon: 'rotateCw', iconBg: 'bg-amber-500', shadow: 'shadow-amber-500/20' },
      { label: 'Total Contracts', value: uniqueContracts.toLocaleString(), icon: 'fileText', iconBg: 'bg-blue-600', shadow: 'shadow-blue-500/20' },
      { label: 'High Value Contracts', value: String(highValueContracts), icon: 'fileText', iconBg: 'bg-purple-600', shadow: 'shadow-purple-500/20' },
      { label: 'Repeat Arrangements %', value: `${repeatThreshold.toFixed(2)}%`, icon: 'repeat', iconBg: 'bg-emerald-500', shadow: 'shadow-emerald-500/20' },
      { label: 'High Risk Contracts', value: String(highRisk), icon: 'triangleAlert', iconBg: 'bg-rose-500', shadow: 'shadow-rose-500/20' },
    ];

    // ── Chart 3: Performance Distribution ────────────────────────────────────
    const bucketCounts = SUCCESS_RATE_BUCKETS.map(b => ({ ...b, count: 0 }));
    records.forEach(r => {
      const rate = r.success_rate || 0;
      const bucket = bucketCounts.find(b => rate >= b.min && rate < b.max);
      if (bucket) bucket.count++;
    });

    // Only include buckets that have admins
    const activeBuckets = bucketCounts.filter(b => b.count > 0);
    this.performanceDistributionData = activeBuckets.map(b => ({
      label: b.label, value: b.count, color: b.color,
    }));

    if (activeBuckets.length > 0) {
      const peakBucket = activeBuckets.reduce((a, b) => b.count > a.count ? b : a, activeBuckets[0]);
      const lowPerformers = bucketCounts
        .filter(b => b.max <= 30)
        .reduce((s, b) => s + b.count, 0);
      const lowPctNum = records.length > 0 ? (lowPerformers / records.length) * 100 : 0;
      const lowPct = `${lowPctNum.toFixed(0)}%`;

      const eliteAdmins = bucketCounts
        .filter(b => b.min >= 50)
        .reduce((s, b) => s + b.count, 0);

      const peakLabel = peakBucket.min >= 70 ? 'Elite Performance' : peakBucket.min >= 50 ? 'Strong Performance' : 'Peak Performance';

      this.performanceStats = [
        { label: peakLabel, value: peakBucket.label, sub: `${peakBucket.count} Admins Concentrated` },
        { label: 'Distribution', value: `${activeBuckets.length} Ranges`, sub: `${eliteAdmins} Admins Above 50%` },
        { label: 'Low Performers', value: lowPct, sub: `${lowPerformers} Admins <30%` },
      ];

      // Decision Insight Logic
      if (lowPctNum >= 30) {
        this.decisionInsight = 'Attention Required: Significant cohort in low-performance bracket.';
      } else if (eliteAdmins / records.length >= 0.4) {
        this.decisionInsight = 'Elite group driving majority of recovery success.';
      } else if (lowPctNum <= 10) {
        this.decisionInsight = 'Healthy base (Low-performance group under control.)';
      } else {
        this.decisionInsight = 'Standard distribution with moderate low-performance presence.';
      }
    }

    // ── Chart 5: Repeat Pattern ────────────────────────────────────────────
    // Bucket admins by total_created (# of arrangements created)
    const arr1 = records.filter(r => r.total_created === 1);
    const arr2_3 = records.filter(r => r.total_created >= 2 && r.total_created <= 3);
    const arr4_5 = records.filter(r => r.total_created >= 4 && r.total_created <= 5);
    const arr6p = records.filter(r => r.total_created > 5);

    const avgRate = (grp: ByAdminRecord[]) =>
      grp.length ? grp.reduce((s, r) => s + (r.success_rate || 0), 0) / grp.length : 0;
    const totalContracts = (grp: ByAdminRecord[]) =>
      grp.reduce((s, r) => s + (r.unique_contracts || 0), 0);

    this.repeatArrangementData = [
      { label: '1st Arr', value: totalContracts(arr1), color: '#3b82f6' },
      { label: '2-3rd Arr', value: totalContracts(arr2_3), color: '#3b82f6' },
      { label: '4-5 Arr', value: totalContracts(arr4_5), color: '#3b82f6' },
      { label: '6+ Arr', value: totalContracts(arr6p), color: '#3b82f6' },
    ];
    this.repeatArrangementLineData = [
      { label: '1st Arr', value: Number(avgRate(arr1).toFixed(1)), color: '#10b981' },
      { label: '2-3rd Arr', value: Number(avgRate(arr2_3).toFixed(1)), color: '#10b981' },
      { label: '4-5 Arr', value: Number(avgRate(arr4_5).toFixed(1)), color: '#10b981' },
      { label: '6+ Arr', value: Number(avgRate(arr6p).toFixed(1)), color: '#10b981' },
    ];

    // ── Chart 7: Admin Productivity ───────────────────────────────────────
    this.adminProductivityData = [...records]
      .sort((a, b) => (b.total_recovered_amount || 0) - (a.total_recovered_amount || 0))
      .slice(0, 15)
      .map(r => ({
        name: r.admin_name,
        role: r.performance_score > 70 ? 'Senior Admin' : r.performance_score > 40 ? 'Admin' : 'Junior Admin',
        volume: r.total_created || 0,
        yield: r.total_recovered_amount || 0,
      }));

    // ── Chart 9: Repeat Intensity Trend ───────────────────────────────────
    // This is now handled dynamically from fetchAllData -> buildRepeatIntensityTrendChart
    // Keeping a fallback or clearing here if needed, but buildAdminPerformanceCharts 
    // shouldn't overwrite the real trend if it's already loading.

    // ── Detailed Stats (Top Performer + Avg Recovery) ─────────────────────
    if (records.length > 0) {
      const topPerformer = this.pickTopPerformer(records);
      const topRecovered = this.getAdminRecoveredAmount(topPerformer);
      const avgRecovery = records.length > 0 ? totalRecovered / records.length : 0;
      const avgPerArr = totalArrangements > 0 ? totalRecovered / totalArrangements : 0;

      this.detailedStats[0] = {
        title: 'Top Performer',
        mainValue: topPerformer.admin_name,
        subValue: '',
        items: [
          { label: 'Recovered', value: `$${topRecovered.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, valueColor: 'text-emerald-500', barColor: 'bg-emerald-500' },
          { label: 'Success Rate', value: `${(topPerformer.success_rate || 0).toFixed(2)}%`, valueColor: 'text-emerald-500', barColor: 'bg-emerald-500' },
        ],
      };
      this.detailedStats[2] = {
        title: 'Avg. Recovery Per Admin',
        mainValue: `$${avgRecovery.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
        subValue: '',
        items: [
          { label: 'Avg. Recovered/Arrangement', value: `$${avgPerArr.toFixed(0)}`, valueColor: 'text-blue-600', barColor: 'bg-blue-600' },
          { label: 'Recovery Efficiency', value: avgRecovery > 5000 ? 'High' : avgRecovery > 1000 ? 'Medium' : 'Low', valueColor: 'text-blue-600', barColor: 'bg-blue-600' },
        ],
      };
    }

    this.refreshLayoutData();
  }

  /**
   * Chart 9: Repeat Arrangement Intensity Trend
   * Source: getCreationTrend (actual time-series)
   */
  private buildRepeatIntensityTrendChart(records: CreationTrendRecord[]): void {
    if (!records.length) {
      this.repeatArrangementTrendData = [];
      return;
    }

    const unit = this.datePickerService.unit();

    // Map time-series to chart format
    this.repeatArrangementTrendData = records.map(r => {
      // Use shared utility for strict parsing with unit hint
      const d = parseToUtc(r.unit_date, unit);
      return {
        start: d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : r.unit_date,
        value: r.total_created || 0,
        key: 'Volume' // Override "Series 1" label in tooltip
      };
    });

    this.refreshLayoutData();
  }

  /**
   * Duplicate Crisis card in detailedStats
   * Source: getDuplicatesSummaryStats
   */
  private buildDuplicateDetailedStats(res: DuplicatesSummaryStatsAPI): void {
    if (!res) return;

    const highDupAdminPct =
      res.admins_involved > 0
        ? `${((res.contracts_with_duplicates / res.admins_involved) * 100).toFixed(2)}%`
        : '0.00%';

    this.detailedStats[1] = {
      title: 'Duplicate Crisis',
      mainValue: (res.total_duplicates || 0).toLocaleString(),
      subValue: '(Total)',
      items: [
        { label: 'Total Null Entries', value: (res.total_null_entries || 0).toLocaleString(), valueColor: 'text-rose-500', barColor: 'bg-rose-500' },
        { label: 'High Duplicate Admins', value: highDupAdminPct, valueColor: 'text-rose-500', barColor: 'bg-rose-500' },
      ],
    };

    this.refreshLayoutData();
  }

  /**
   * Chart 4: Recovery Efficiency Line Chart
   * Source: getRecoveryStatus (time-series amounts) + getSuccessRateTrend (rate splits)
   *
   * Produces two line-area series:
   *   - "Recurring Recovery" — recovered_amount × (recurring_success_rate / total_rate)
   *   - "Partial Recovery"   — recovered_amount × (partial_success_rate  / total_rate)
   */
  buildRecoveryEfficiencyData(
    statusRecords: RecoveryStatusRecord[],
    srRecords: SuccessRateTrendRecord[]
  ): void {
    if (!statusRecords.length) return;

    // Build a quick lookup: unit_date → success-rate record
    const srMap = new Map<string, SuccessRateTrendRecord>();
    srRecords.forEach(r => srMap.set(r.unit_date, r));

    // Sort status records chronologically
    const sorted = [...statusRecords].sort((a, b) => {
      const da = parseToUtc(a.unit_date, this.datePickerService.unit());
      const db = parseToUtc(b.unit_date, this.datePickerService.unit());
      return da.valueOf() - db.valueOf();
    });

    const recurringPoints: { date: string; value: number }[] = [];
    const partialPoints: { date: string; value: number }[] = [];

    sorted.forEach(r => {
      const totalAmount = Number(r.recovered_amount) || 0;
      const sr = srMap.get(r.unit_date);

      // Split proportionally; fall back to 50/50 when rate data is unavailable
      const recurringRate = sr ? (Number(sr.recurring_success_rate) || 0) : 50;
      const partialRate = sr ? (Number(sr.partial_success_rate) || 0) : 50;
      const totalRate = recurringRate + partialRate || 1; // avoid div/0

      const recurringAmt = Math.round(totalAmount * (recurringRate / totalRate));
      const partialAmt = Math.round(totalAmount * (partialRate / totalRate));

      const normDate = parseToUtc(r.unit_date, this.datePickerService.unit()).format('YYYY-MM-DD HH:mm:ss');
      recurringPoints.push({ date: normDate, value: recurringAmt });
      partialPoints.push({ date: normDate, value: partialAmt });
    });

    this.recoveryEfficiencyData = [
      {
        key: 'recurring',
        name: 'Recurring Recovery',
        data: recurringPoints,
        areaColor: '#d946ef',
        lineColor: '#d946ef',
        areaOpacity: 0,
        showPoints: true,
      },
      {
        key: 'partial',
        name: 'Partial Recovery',
        data: partialPoints,
        areaColor: '#3b82f6',
        lineColor: '#3b82f6',
        areaOpacity: 0,
        showPoints: true,
      },
    ];
  }

  private getUnitLabel(unit: string): string {
    switch (unit) {
      case 'day': return 'DoD';
      case 'week': return 'WoW';
      case 'month': return 'MoM';
      case 'year': return 'YoY';
      default: return 'YoY';
    }
  }

  private getFullUnitLabel(unit: string): string {
    switch (unit) {
      case 'day': return 'Day-Over-Day';
      case 'week': return 'Week-Over-Week';
      case 'month': return 'Month-Over-Month';
      case 'year': return 'Year-Over-Year';
      default: return 'Year-Over-Year';
    }
  }

  // ─── Layout ───────────────────────────────────────────────────────────────

  private initializeLayout(): void {
    this.contentRows = [
      { type: 'stats', gridClass: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4 mb-3', items: this.performanceCards },
      { type: 'detailed-stats', gridClass: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4', items: this.detailedStats },
      {
        type: 'charts',
        gridClass: 'grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4',
        items: [
          { id: 'duplicate-over-time', title: 'Duplicate Entry Trend Over Time', subtitle: 'Monitoring Duplicate Submission Patterns Over Time', type: 'multiple-bar', height: '300px', legendPosition: 'center', legendGap: 160 },
          { id: 'duplicate-yoy', title: 'Duplicate Trend YoY', subtitle: 'Year-Over-Year Comparison of Duplicate Entries', type: 'line-area', height: '300px' },
        ],
      },
      {
        type: 'charts',
        gridClass: 'grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4',
        items: [
          { id: 'performance-distribution', title: 'Performance Distribution - Admin Success Rate', subtitle: 'Admin success performance overview', type: 'isometric-bar', height: 'auto' },
          { id: 'recovery-efficiency', title: 'Recovery Efficiency', subtitle: 'Measuring Effectiveness of Recovery Outcomes', type: 'line-area', height: '420px' },
        ],
      },
      {
        type: 'charts',
        gridClass: 'grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4',
        items: [
          { id: 'repeat-pattern', title: 'Repeat Arrangement Pattern Analysis', subtitle: 'Contracts vs contract success rate', type: 'lollipop', height: '300px', minHeight: 360, marginTop: -5, marginBottom: 70, legendOffset: 60 },
          { id: 'admin-workload', title: 'Admin Workload vs Recovery Output', subtitle: 'Workload distribution across admins', type: 'horizontal-bar', height: '280px', minHeight: 300, marginBottom: 0 },
        ],
      },
      {
        type: 'charts',
        gridClass: 'grid grid-cols-1 gap-4 mt-4',
        items: [
          { id: 'admin-productivity', title: 'Admin Productivity vs Recovery Yield', subtitle: 'Efficiency tracking per admin', type: 'vertical-single-bar', height: 'auto' },
        ],
      },
      {
        type: 'charts',
        gridClass: 'grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4',
        items: [
          { id: 'duplicates-by-admin', title: 'Duplicate Entries by Admin (Top 5)', subtitle: 'Leading admins by duplicate count', type: 'multiple-bar', height: '300px', legendPosition: 'center', legendGap: 160 },
          { id: 'repeat-intensity', title: 'Repeat Arrangement Intensity Trend', subtitle: 'YoY Arrangement Attempt Trends & Stabilization', type: 'line-chart', height: '300px', minHeight: 300, marginTop: 20, marginBottom: 40 },
        ],
      },
      { type: 'table', title: 'Admin Duplicate Exposure', subtitle: '% of contracts impacted by duplicates (by admin)', tableKey: 'exposure', data: this.adminExposureData, headers: this.exposureHeaders },
      { type: 'table', title: 'Detailed Duplicate Analysis', subtitle: 'by Admin', tableKey: 'detailed', data: this.detailedDuplicateData, headers: this.detailedHeaders },
      { type: 'year-summary', gridClass: 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 mt-6', items: this.yearSummaryCards() },
    ];
  }

  /** Push latest arrays into the layout rows (avoids recreating full layout) */
  private refreshLayoutData(): void {
    const statsRow = this.contentRows.find(r => r.type === 'stats');
    const detailRow = this.contentRows.find(r => r.type === 'detailed-stats');
    const yearRow = this.contentRows.find(r => r.type === 'year-summary');
    const exposureRow = this.contentRows.find(r => r.type === 'table' && r.tableKey === 'exposure');
    const detailTable = this.contentRows.find(r => r.type === 'table' && r.tableKey === 'detailed');

    if (statsRow) statsRow.items = this.performanceCards;
    if (detailRow) detailRow.items = this.detailedStats;
    if (yearRow) yearRow.items = this.yearSummaryCards();
    if (exposureRow) exposureRow.data = this.adminExposureData;
    if (detailTable) detailTable.data = this.detailedDuplicateData;

    // Update Duplicate Trend title/subtitle based on current unit
    const chartsRow = this.contentRows.find(r => r.type === 'charts' && r.items?.some(i => i.id === 'duplicate-yoy'));
    if (chartsRow) {
      const item = chartsRow.items?.find(i => i.id === 'duplicate-yoy');
      if (item) {
        const u = this.unit();
        const label = this.getUnitLabel(u);
        const fullLabel = this.getFullUnitLabel(u);
        item.title = `Duplicate Trend ${label}`;
        item.subtitle = `${fullLabel} Comparison of Duplicate Entries`;
      }
    }

    // Update Repeat Intensity title/subtitle
    const repeatTrendRow = this.contentRows.find(r => r.type === 'charts' && r.items?.some(i => i.id === 'repeat-intensity'));
    if (repeatTrendRow) {
      const item = repeatTrendRow.items?.find(i => i.id === 'repeat-intensity');
      if (item) {
        const u = this.unit();
        const label = this.getUnitLabel(u);
        item.title = `Repeat Arrangement Intensity ${label}`;
        item.subtitle = `${label} Arrangement Attempt Trends & Stabilization`;
      }
    }
    this.cdr.markForCheck();
  }

  // ─── Chart Data Presence ────────────────────────────────────────────────
  isLoading(id: string): boolean {
    switch (id) {
      case 'duplicate-over-time':
      case 'duplicate-yoy':
        return this.isLoadingDuplicatesTrend;
      case 'performance-distribution':
      case 'repeat-pattern':
      case 'admin-productivity':
        return this.isLoadingByAdmin;
      case 'recovery-efficiency':
        return this.isLoadingRecoveryStatus;
      case 'admin-workload':
      case 'duplicates-by-admin':
        return this.isLoadingDuplicatesByAdmin;
      case 'repeat-intensity':
        return this.isLoadingRepeatTrend;
      default:
        return false;
    }
  }

  hasData(id: string): boolean {
    switch (id) {
      case 'duplicate-over-time':
        return (this.duplicateOverTimeData.data1?.length || 0) > 0;
      case 'duplicate-yoy':
        return (this.duplicateYoYData[0]?.data?.length || 0) > 0;
      case 'performance-distribution':
        return (this.performanceDistributionData?.length || 0) > 0;
      case 'recovery-efficiency':
        return (this.recoveryEfficiencyData[0]?.data?.length || 0) > 0;
      case 'repeat-pattern':
        return (this.repeatArrangementData?.length || 0) > 0;
      case 'admin-workload':
        return (this.adminWorkloadData?.length || 0) > 0;
      case 'admin-productivity':
        return (this.adminProductivityData?.length || 0) > 0;
      case 'duplicates-by-admin':
        return (this.duplicatesData?.length || 0) > 0;
      case 'repeat-intensity':
        return (this.repeatArrangementTrendData?.length || 0) > 0;
      default:
        return false;
    }
  }

  // ─── Panel ────────────────────────────────────────────────────────────────

  openLogsPanel(type: string): void {
    this.isPanelOpen = true;
    if (type === 'exposure') {
      this.panelConfig = {
        title: 'Admin Duplicate Exposure',
        subtitle: '% of contracts impacted by duplicates',
        data: this.adminExposureData.map((d, i) => ({ ...d, id: i + 1 })),
        columns: [
          { key: 'name', label: 'Admin', type: 'text' },
          { key: 'affected', label: 'Contracts Affected', type: 'text' },
          { key: 'rate', label: 'Admin Duplicate Rate', type: 'text' },
        ],
        accentColor: 'indigo',
      };
    } else if (type === 'detailed') {
      this.panelConfig = {
        title: 'Detailed Duplicate Analysis',
        subtitle: 'Breakdown by admin',
        data: this.detailedDuplicateData.map((d, i) => ({ ...d, id: i + 1 })),
        columns: [
          { key: 'name', label: 'Admin', type: 'text' },
          { key: 'nullEntries', label: 'Null Entries', type: 'text' },
          { key: 'duplicates', label: 'Duplicates', type: 'text' },
          { key: 'deletions', label: 'Deletions', type: 'text' },
          { key: 'contracts', label: 'Contracts', type: 'text' },
          { key: 'avg', label: 'Avg Per Contract', type: 'text' },
          { key: 'impact', label: 'Impact', type: 'badge' },
        ],
        accentColor: 'rose',
      };
    }
  }

  onClosePanel(): void {
    this.isPanelOpen = false;
    this.panelConfig = null;
  }

  handleAdminProductivityViewAll(): void {
    const sorted = [...this.allAdminPerformanceRecords].sort((a, b) => (b.total_recovered_amount || 0) - (a.total_recovered_amount || 0));

    this.panelConfig = {
      title: 'Admin Productivity Details',
      subtitle: 'Comprehensive performance and yield by administrator',
      accentColor: 'emerald',
      data: sorted.map(r => ({
        admin_name: r.admin_name,
        role: r.performance_score > 70 ? 'Senior Admin' : r.performance_score > 40 ? 'Admin' : 'Junior Admin',
        volume: r.total_created || 0,
        yield: r.total_recovered_amount || 0,
        success_rate: ((r.success_rate || 0)).toFixed(2) + '%',
        contracts: r.unique_contracts || 0
      })),
      columns: [
        { key: 'admin_name', label: 'Administrator', type: 'text' },
        { key: 'role', label: 'Role', type: 'badge' },
        { key: 'volume', label: 'Total Arrangements', type: 'text' },
        { key: 'yield', label: 'Total Recovered', type: 'currency' },
        { key: 'success_rate', label: 'Success Rate', type: 'text' },
        { key: 'contracts', label: 'Unique Contracts', type: 'text' }
      ]
    };
    this.isPanelOpen = true;
  }

  openAdminDetails(row: any, tableData?: any[]): void {
    if (row && row.admin_id) {
      this.selectedAdminId = row.admin_id;

      // Extract options from the specific table data instead of the global data
      if (tableData && Array.isArray(tableData) && tableData.length > 0) {
        this.adminOptions = tableData
          .filter(r => r.admin_id && r.name || r.admin_name)
          .map(admin => ({
            value: admin.admin_id.toString(),
            label: admin.admin_name || admin.name || 'Unknown Admin'
          }));
      } else {
        // Fallback to the global records if the table data somehow isn't passed
        this.adminOptions = this.allAdminPerformanceRecords.map(admin => ({
          value: admin.admin_id.toString(),
          label: admin.admin_name
        }));
      }

      this.dialog.open(this.adminDetailsModal, {
        panelClass: ['admin-details-panel', '!p-0', '!bg-transparent', '!shadow-none', '!border-none', '!outline-none'],
        backdropClass: ['bg-slate-900/60', 'backdrop-blur-sm'],
        maxWidth: '100vw',
        maxHeight: '100vh'
      });
    }
  }

  // ─── Pin handlers ──────────────────────────────────────────────────────
  private _toSectionId(
    sec:
      | { id: ArrangementCronAdminPerformanceSectionId }
      | ArrangementCronAdminPerformanceSectionId
      | string,
  ): ArrangementCronAdminPerformanceSectionId | null {
    if (typeof sec === 'string') {
      return isArrangementCronAdminPerformanceSectionId(sec) ? sec : null;
    }
    return sec?.id ?? null;
  }

  isAddedToCustomDashboard(
    sec:
      | { id: ArrangementCronAdminPerformanceSectionId }
      | ArrangementCronAdminPerformanceSectionId
      | string,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pinnedSections().has(id) : false;
  }

  isAddingToCustomDashboard(
    sec:
      | { id: ArrangementCronAdminPerformanceSectionId }
      | ArrangementCronAdminPerformanceSectionId
      | string,
  ): boolean {
    const id = this._toSectionId(sec);
    return id ? this.pendingPinSections().has(id) : false;
  }

  canPinToCustomDashboard(
    sec:
      | { id: ArrangementCronAdminPerformanceSectionId }
      | ArrangementCronAdminPerformanceSectionId
      | string,
  ): boolean {
    if (this.mixedMode()) return false;
    return !!this._toSectionId(sec);
  }

  addToCustomDashboard(
    sec:
      | { id: ArrangementCronAdminPerformanceSectionId }
      | ArrangementCronAdminPerformanceSectionId
      | string,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toaster?.info?.(
        'Pinning Arrangement Cron admin performance sections to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.catalogApiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Layout editor (public entry for parent shell) ─────────────────────
  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: AdminPerformanceLayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      defaultColSpan: this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      ),
    }));

    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: string): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: AdminPerformanceLayoutEditorItem[] = this.SECTIONS.map((s, idx) => {
      const defaultSpan = this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      );
      return {
        id: s.id as ArrangementCronAdminPerformanceSectionId,
        title: s.title,
        visible: true,
        colSpan: defaultSpan,
        order: idx,
        defaultColSpan: defaultSpan,
      };
    });
    this.editorItems.set(defaults);
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    if (!draft.some((d) => d.visible)) {
      this.toaster?.warning?.(
        'At least one section must be visible. Toggle a section on before applying.',
      );
      return;
    }

    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.toaster?.success?.('Layout updated for Admin Performance.');

    this.isLayoutEditorOpen.set(false);
  }

  decreaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[i.id] ?? 12,
        );
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  onEditorCardDrop(event: CdkDragDrop<AdminPerformanceLayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (!next) return;
    this.editorItems.set(next);
    this._syncLayoutFromEditor();
  }

  private _buildLayoutFromEditor(): Record<
    ArrangementCronAdminPerformanceSectionId,
    AdminPerformanceSectionLayoutItem
  > {
    const map = buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS,
      ARRANGMENT_CRON_DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
    return map as Record<
      ArrangementCronAdminPerformanceSectionId,
      AdminPerformanceSectionLayoutItem
    >;
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<
      ArrangementCronAdminPerformanceSectionId,
      AdminPerformanceSectionLayoutItem
    > = {} as Record<
      ArrangementCronAdminPerformanceSectionId,
      AdminPerformanceSectionLayoutItem
    >;

    ArrangementCronAdminPerformanceSectionDef.forEach((s, idx) => {
      const sid = s.id as ArrangementCronAdminPerformanceSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    if (!Object.values(layout).some((l) => l.visible)) {
      ArrangementCronAdminPerformanceSectionDef.forEach((s, idx) => {
        const sid = s.id as ArrangementCronAdminPerformanceSectionId;
        layout[sid] = {
          id: sid,
          visible: true,
          colSpan: this._normalizeColSpan(ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12),
          order: idx,
        };
      });
    }

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, AdminPerformanceSectionLayoutItem> | null {
    try {
      const raw =
        localStorage.getItem(this.LAYOUT_LS_KEY) ??
        localStorage.getItem('prism_arrangment_cron_ac_admin_performance_layout_v2') ??
        localStorage.getItem('prism_arrangment_cron_ac_admin_performance_layout_v1');
      return raw ? this._migrateLegacyAdminLayout(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private _migrateLegacyAdminLayout(
    stored: Record<string, AdminPerformanceSectionLayoutItem>,
  ): Record<string, AdminPerformanceSectionLayoutItem> {
    const migrated = { ...stored };
    const legacyKeyMap: Record<string, ArrangementCronAdminPerformanceSectionId> = {
      ap_kpi_cards: ArrangementCronAdminPerformanceSectionId.STATS_SUMMARY_OVERVIEW,
      ap_duplicate_over_time: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRY_TREND_OVER_TIME,
      ap_duplicate_yoy: ArrangementCronAdminPerformanceSectionId.DUPLICATE_TREND_DOD,
      ap_performance_distribution: ArrangementCronAdminPerformanceSectionId.PERFORMANCE_DISTRIBUTION_SUCCESS,
      ap_recovery_efficiency: ArrangementCronAdminPerformanceSectionId.RECOVERY_EFFICIENCY,
      ap_repeat_pattern: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_PATTERN,
      ap_admin_workload: ArrangementCronAdminPerformanceSectionId.ADMIN_WORKLOAD_VS_RECOVERY,
      ap_admin_productivity: ArrangementCronAdminPerformanceSectionId.ADMIN_PRODUCTIVITY_VS_RECOVERY,
      ap_duplicates_by_admin: ArrangementCronAdminPerformanceSectionId.DUPLICATE_ENTRIES_BY_ADMIN,
      ap_repeat_intensity: ArrangementCronAdminPerformanceSectionId.REPEAT_ARRANGEMENT_INTENSITY_DOD,
      ap_admin_exposure: ArrangementCronAdminPerformanceSectionId.ADMIN_DUPLICATE_EXPOSURE,
      ap_detailed_duplicate: ArrangementCronAdminPerformanceSectionId.DETAILED_DUPLICATE_ANALYSIS,
    };

    for (const [legacyKey, nextKey] of Object.entries(legacyKeyMap)) {
      if (migrated[legacyKey] && !migrated[nextKey]) {
        migrated[nextKey] = { ...migrated[legacyKey], id: nextKey };
      }
      delete migrated[legacyKey];
    }

    const detailedLegacy = migrated['ap_detailed_stats'];
    if (detailedLegacy) {
      for (const nextKey of [
        ArrangementCronAdminPerformanceSectionId.TOP_PERFORMER,
        ArrangementCronAdminPerformanceSectionId.DUPLICATE_CRISIS,
        ArrangementCronAdminPerformanceSectionId.AVG_RECOVERY_PER_ADMIN,
      ]) {
        if (!migrated[nextKey]) {
          migrated[nextKey] = { ...detailedLegacy, id: nextKey };
        }
      }
      delete migrated['ap_detailed_stats'];
    }

    const yearLegacy = migrated['ap_year_summary'];
    if (yearLegacy) {
      for (const nextKey of [
        ArrangementCronAdminPerformanceSectionId.WORST_YEAR,
        ArrangementCronAdminPerformanceSectionId.BEST_YEAR,
        ArrangementCronAdminPerformanceSectionId.LATEST_YEAR,
      ]) {
        if (!migrated[nextKey]) {
          migrated[nextKey] = { ...yearLegacy, id: nextKey };
        }
      }
      delete migrated['ap_year_summary'];
    }

    return migrated;
  }

  private _saveLayoutToStorage(
    layout: Record<ArrangementCronAdminPerformanceSectionId, AdminPerformanceSectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    return normalizeLayoutEditorColSpan(span);
  }

  private _persistLayoutToBackend(
    draft: ReadonlyArray<AdminPerformanceLayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.catalogApiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) return;

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
